import { i18n } from '@fs/zion-locale'
import { hasJapaneseName, hasCJKName } from '../../../helpers/keep/utils'

export function hasVisibleParent(pedigree) {
  const father = getFather(pedigree?.parents)
  const mother = getMother(pedigree?.parents)

  const isFatherVisible = father?.person?.metadata?.visibility?.visible && father?.person?.id
  const isMotherVisible = mother?.person?.metadata?.visibility?.visible && mother?.person?.id
  return isFatherVisible || isMotherVisible
}

export function getFather(parents) {
  if (!parents) return null
  return parents.parent1Pedigree
}

export function getMother(parents) {
  if (!parents) return null
  return parents.parent2Pedigree
}

export function getPreferredSpouse(pairings) {
  return pairings?.find(
    (pairing) => pairing?.spousePedigree?.person?.metadata?.visibility?.visible && pairing?.spousePedigree?.person?.id
  )?.spousePedigree?.person
}

export function getSpouses(pairings) {
  return pairings?.map((pairing) => pairing?.spousePedigree?.person).filter((person) => person?.id) || []
}

export function getPOF(pedigree) {
  return pedigree?.person || null
}

export function getFullSiblings(fullSiblingPedigrees) {
  return fullSiblingPedigrees?.map((sibling) => sibling?.person).filter((person) => person?.id) || []
}

// Note: In 'pairings' both the spouses children and the POF children are included.
// A child in each of these can be the same person.
// This function returns a unique set of children.
export function getChildren(pairings) {
  return (
    pairings
      ?.map((pairing) => pairing?.childPedigrees)
      ?.flat()
      .map((childPedigree) => childPedigree?.person?.id && childPedigree?.person)
      .filter(Boolean)
      // If there is an element with the same id before the current index, it is a duplicate
      .filter((person, index, self) => self.findIndex((currentPerson) => currentPerson.id === person.id) === index)
      // We sort the whole list of children (all spouses) in order by birthdate. If no birthdate, then .sort() defaults to alphabetical
      .sort((a, b) => (a.lifespanBegin?.date?.original ?? 0) - (b.lifespanBegin?.date?.original ?? 0)) || []
  )
}

export function getRelationshipText(leafNumber) {
  switch (leafNumber) {
    case Number.parseInt(leafNumber, 10) === -1 ? leafNumber : null:
      return i18n.t('tree.relationshipText.child', 'Child')
    case '0':
      return i18n.t('tree.relationshipText.self', 'Self')
    case Number.parseInt(leafNumber, 10) === 1 ? leafNumber : null:
      return i18n.t('tree.relationshipText.spouse', 'Spouse')
    case '2':
      return i18n.t('tree.relationshipText.father', 'Father')
    case '3':
      return i18n.t('tree.relationshipText.mother', 'Mother')
    case '4':
      return i18n.t('tree.relationshipText.maternalGrandfather', 'Grandfather')
    case '5':
      return i18n.t('tree.relationshipText.maternalGrandmother', 'Grandmother')
    case '6':
      return i18n.t('tree.relationshipText.maternalGrandmothersFather', 'Great-Grandfather')
    case '7':
      return i18n.t('tree.relationshipText.maternalGrandmothersMother', 'Great-Grandmother')
    case '12':
      return i18n.t('tree.relationshipText.maternalGrandfathersFather', 'Great-Grandfather')
    case '13':
      return i18n.t('tree.relationshipText.maternalGrandfathersMother', 'Great-Grandmother')
    case '18':
      return i18n.t('tree.relationshipText.paternalGrandfather', 'Grandfather')
    case '19':
      return i18n.t('tree.relationshipText.paternalGrandmother', 'Grandmother')
    case '20':
      return i18n.t('tree.relationshipText.paternalGrandmothersFather', 'Great-Grandfather')
    case '21':
      return i18n.t('tree.relationshipText.paternalGrandmothersMother', 'Great-Grandmother')
    case '26':
      return i18n.t('tree.relationshipText.paternalGrandfathersFather', 'Great-Grandfather')
    case '27':
      return i18n.t('tree.relationshipText.paternalGrandfathersMother', 'Great-Grandmother')
    default:
      return i18n.t('tree.relationshipText.unknown', 'Unknown')
  }
}

export function getRelatives(pedigree) {
  const relatives = getRawRelatives(pedigree)
  return relatives.map((person) => getSelectValues(person)).filter(Boolean) || []
}

export function getRawRelatives(pedigree) {
  if (!pedigree) return []

  const { pairings, parents, person } = pedigree
  const spouses = getSpouses(pairings)
  const children = getChildren(pairings)
  const father = getFather(parents)
  const mother = getMother(parents)
  // grandparents from left to right
  const gp1 = getFather(father?.parents)
  const gp2 = getMother(father?.parents)
  const gp3 = getFather(mother?.parents)
  const gp4 = getMother(mother?.parents)
  // great-grandparents from left to right
  const ggp1 = getFather(gp1?.parents)?.person
  const ggp2 = getMother(gp1?.parents)?.person
  const ggp3 = getFather(gp2?.parents)?.person
  const ggp4 = getMother(gp2?.parents)?.person
  const ggp5 = getFather(gp3?.parents)?.person
  const ggp6 = getMother(gp3?.parents)?.person
  const ggp7 = getFather(gp4?.parents)?.person
  const ggp8 = getMother(gp4?.parents)?.person
  return (
    [
      person,
      ...spouses,
      ...children,
      father?.person,
      mother?.person,
      gp1?.person,
      gp2?.person,
      gp3?.person,
      gp4?.person,
      ggp1,
      ggp2,
      ggp3,
      ggp4,
      ggp5,
      ggp6,
      ggp7,
      ggp8,
    ].filter(Boolean) || []
  )
}

function mapLeafNumberToPosition(leafNumber) {
  // positions are ordered from left to right from generation 1 to 5
  // leaf numbers recurse getting father and then mother and the moving up mother to get her father and mother etc.
  const leafNumberToPosition = {
    0: 1,
    2: 2,
    3: 3,
    4: 6,
    5: 7,
    6: 14,
    7: 15,
    8: 30,
    9: 31,
    10: 28,
    11: 29,
    12: 12,
    13: 13,
    14: 26,
    15: 27,
    16: 24,
    17: 25,
    18: 4,
    19: 5,
    20: 10,
    21: 11,
    22: 22,
    23: 23,
    24: 20,
    25: 21,
    26: 8,
    27: 9,
    28: 18,
    29: 19,
    30: 16,
    31: 17,
  }
  return leafNumberToPosition[leafNumber]
}

const SPOUSE_PEDIGREE_POSITION_OFFSET = 31
const INITIAL_SPOUSE_POSITION = 32
const INITIAL_PERSON_POSITION = 1

// If spouse pedigree, then we offset the position by 31 to avoid overlap with the pof's ancestor's positions
export function formatTreePersons({ pedigree, isSpousePedigree = false }) {
  if (!pedigree) {
    return []
  }
  pedigree.person.position = isSpousePedigree ? INITIAL_SPOUSE_POSITION : INITIAL_PERSON_POSITION
  const treePersons = [pedigree.person]
  function formatParent(parentData, childData, spouseData) {
    if (parentData) {
      const parent = parentData.person
      parent.child = childData.person
      if (spouseData) {
        parent.spouse = spouseData.person
      }
      // leaf tags of spouse ancestors follow the same pattern as the pof's ancestor's leaf number but with 1.0 prepended
      // ex: pof's mother's leaf number is 3, then preferred spouse's mother's leaf number is 1.0.3
      parent.position = isSpousePedigree
        ? mapLeafNumberToPosition(Number.parseInt(parent.metadata.leafNumber.split('.').pop(), 10)) +
          SPOUSE_PEDIGREE_POSITION_OFFSET
        : mapLeafNumberToPosition(parent.metadata.leafNumber)
      treePersons.push(parent)
      formatParents(parentData)
    }
  }

  function formatParents(personData) {
    const fatherData = getFather(personData.parents)
    const motherData = getMother(personData.parents)

    formatParent(fatherData, personData, motherData)
    formatParent(motherData, personData, fatherData)
  }

  formatParents(pedigree)

  const sortedTreePersons = treePersons.sort((a, b) => (a.position > b.position ? 1 : -1))

  return sortedTreePersons
}

export function formatArtisticDisplayName({ design, name, person }) {
  const designsWithEightChars = ['koi', 'coloringForestAnimals', 'forestAnimals', 'spaceAliens']
  const maxChar = designsWithEightChars.includes(design) ? 8 : 5

  let firstGivenName = null
  const primaryNameForm = person?.primaryNameForm
  if (primaryNameForm?.parts) {
    const givenNamePart = primaryNameForm?.parts?.find((part) => part?.type?.toLowerCase().includes('given'))
    if (givenNamePart?.value) {
      firstGivenName = givenNamePart.value.split(' ')[0]
    }
  }

  let displayName = {
    part1: name,
    firstGivenName: firstGivenName || name,
  }

  if (!name) return displayName

  // Split on spaces if Japanese name because p tags can't wrap spaces for Japanese
  if (hasJapaneseName(person) && name.length > maxChar) {
    // Note: Japanese almost never have middle names.
    // If there are multiple spaces it probably means the name includes a title.
    // We split on the last space and if there is still a space in part1, we remove it.
    // This handles cases where the name is "wife of so-and-so".
    // See: LCMJ-F74 or 92MF-DF6
    // Instead of 伊藤 源太郎 夫人 we get 伊藤源太郎 夫人, which seems to be the preferred format.
    const spaceIndex = name.lastIndexOf(' ')
    if (spaceIndex > 0) {
      const part1 = name.slice(0, spaceIndex).replace(/\s+/g, '')
      const part2 = name.slice(spaceIndex + 1)
      displayName = { part1, part2, firstGivenName: displayName.firstGivenName }
      return displayName
    }
  }

  // Split by character for names without spaces (typically Chinese and Korean names)
  if (hasCJKName(person) && !name.includes(' ') && name.length > maxChar) {
    const splitIndex = name.length === maxChar + 1 ? maxChar - 1 : maxChar
    const part1 = name.slice(0, splitIndex)
    const part2 = name.slice(splitIndex)
    displayName = { part1, part2, firstGivenName: displayName.firstGivenName }
  }

  return displayName
}

function getSelectValues(person) {
  const value = person?.id || person?.person?.id
  const label = person?.name || person?.person?.name
  if (!value || !label) return null
  return { label, value }
}

export function personHasSpouseOrChildren(pedigree) {
  return !!(
    getChildren(pedigree?.pairings).length ||
    getSpouses(pedigree?.pairings).length ||
    pedigree?.metadata.generations > 3
  )
}

export function visibleGenerations(gvp) {
  if (gvp < 1) return 1
  return Math.floor(Math.log2(gvp)) + 1
}

export function greatestVisiblePosition(child, parents) {
  if (child?.metadata?.visibility?.visible === true) {
    const greatestMaternalPosition = greatestVisiblePosition(parents?.[0][0], parents?.[0][1]?.[0])
    const greatestPaternalPosition = greatestVisiblePosition(parents?.[1][0], parents?.[1][1]?.[0])
    return Math.max(greatestMaternalPosition, greatestPaternalPosition, child.position)
  }
  return -1
}
